import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { Subheading } from "../../../design/heading";
import { Divider } from "../../../design/divider";
import { Text } from "../../../design/text";
import { Badge } from "../../../design/badge";
import { Button } from "../../../design/button";
import { Avatar } from "../../../design/avatar";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../../../design/dialog";
import { getInitials } from "../../../utils/string";
import moment from "moment";
import { SignInSheet, SignInEntry } from "@visitproof/types";
import {
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  CameraIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

interface SignInSheetCardProps {
  visitId: string;
  accountId: string;
}

const SignInSheetCard: React.FC<SignInSheetCardProps> = ({
  visitId,
  accountId,
}) => {
  const [sheet, setSheet] = useState<SignInSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<SignInEntry | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, "signInSheets"),
      where("visitId", "==", visitId),
      where("accountId", "==", accountId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setSheet({ id: doc.id, ...doc.data() } as SignInSheet);
        } else {
          setSheet(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error loading sign-in sheet:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [visitId, accountId]);

  if (loading || !sheet || !sheet.entries) return null;

  const entries = Object.values(sheet.entries);
  const checkedIn = entries.filter((e) => e.isPresent).length;

  return (
    <>
      <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
            <Subheading>Sign-In Sheet</Subheading>
          </div>
          {sheet.isComplete ? (
            <Badge color="green">All Signed In</Badge>
          ) : (
            <Badge color="amber">
              {checkedIn} of {entries.length}
            </Badge>
          )}
        </div>
        <Divider className="my-4" />

        <div className="space-y-3">
          {entries.map((entry) => (
            <div
              key={entry.parentId}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                entry.isPresent
                  ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  : "bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
              }`}
            >
              {/* Photo or avatar */}
              {entry.photoUrl ? (
                <button
                  onClick={() => setSelectedEntry(entry)}
                  className="flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <img
                    src={entry.photoUrl}
                    alt={entry.parentName}
                    className="w-10 h-10 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                  />
                </button>
              ) : (
                <Avatar
                  className="size-10"
                  initials={getInitials(entry.parentName)}
                />
              )}

              {/* Name and status */}
              <div className="flex-1 min-w-0">
                <Text
                  className={`font-medium ${
                    entry.isPresent
                      ? "text-green-800 dark:text-green-200"
                      : "text-zinc-700 dark:text-zinc-300"
                  }`}
                >
                  {entry.parentName}
                </Text>
                {entry.isPresent && entry.checkedInAt && (
                  <Text className="text-xs text-green-600 dark:text-green-400">
                    Signed in at{" "}
                    {moment((entry.checkedInAt as any).toDate()).format(
                      "h:mm A"
                    )}
                  </Text>
                )}
              </div>

              {/* View details button */}
              {entry.isPresent && (
                <div className="flex items-center gap-2">
                  {(entry.photoUrl || entry.signatureUrl) && (
                    <button
                      onClick={() => setSelectedEntry(entry)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      View
                    </button>
                  )}
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                </div>
              )}

              {!entry.isPresent && (
                <Text className="text-sm text-zinc-400 dark:text-zinc-500 italic">
                  Not signed in
                </Text>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Photo & Signature Viewer Dialog */}
      <Dialog
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        size="2xl"
      >
        <DialogTitle>
          {selectedEntry?.parentName} — Check-In Details
        </DialogTitle>
        <DialogBody>
          {selectedEntry && (
            <div className="space-y-6">
              {/* Check-in time */}
              {selectedEntry.checkedInAt && (
                <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  Signed in at{" "}
                  {moment(
                    (selectedEntry.checkedInAt as any).toDate()
                  ).format("MMMM Do, YYYY [at] h:mm A")}
                </div>
              )}

              {/* Photo */}
              {selectedEntry.photoUrl && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CameraIcon className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                    <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Check-In Photo
                    </Text>
                  </div>
                  <img
                    src={selectedEntry.photoUrl}
                    alt={`${selectedEntry.parentName} check-in photo`}
                    className="w-full max-h-96 object-contain rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800"
                  />
                </div>
              )}

              {/* Signature */}
              {selectedEntry.signatureUrl && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <PencilSquareIcon className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                    <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Signature
                    </Text>
                  </div>
                  <div className="p-4 bg-white border border-zinc-200 dark:border-zinc-700 rounded-lg">
                    <img
                      src={selectedEntry.signatureUrl}
                      alt={`${selectedEntry.parentName} signature`}
                      className="max-h-40 mx-auto object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setSelectedEntry(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignInSheetCard;
